import { useContext, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useApi from 'hooks/useApi'
import appContext from 'context/appContext'
import ProjectPageContext from 'context/projectContext/projectContext'
import { ProductContext } from 'context/productContext/productContext'
import { WppBreadcrumb, WppTypography } from '@wppopen/components-library-react'
import PageHeader from 'components/PageHeader/PageHeader'
import { MenuItems } from 'components/MenuListPopUp/MenuListPopUp'
import { FormSkeleton } from 'components/Skeletons'
import NotFound404Page from 'pages/NotFound404Page/NotFound404Page'
import VariantDataForm from 'components/Forms/VariantDataForm/VariantDataForm'
import UnsavedChangesModal from 'components/Modals/UnsavedChangesModal/UnsavedChangesModal'
import clsx from 'clsx'
import Banner from 'components/Banner'
import usePermissions from 'hooks/usePermissions'
import useInitialiser from 'hooks/useInitialiser'
import { statusChecker } from 'components/StatusMessage/Status'
import { masterSVG } from 'components/VariantData/utils'
import useToast from 'hooks/useToast'
import { unknown } from 'utils/constants'
import { createCsApiUrlFromEndpoint, getFormattedDate } from 'utils'
import type { WppBreadcrumbCustomEvent } from '@wppopen/components-library/dist/types/components'
import type { BreadcrumbItemEventDetails } from '@wppopen/components-library'
import type { FEApiError } from 'utils/errorHandling'
import type { VariantsDetailData } from '@contracts'
import styles from '../ProjectView.module.scss'
import navigationHeaderStyles from '../../Settings/NavigationHeader.module.scss'

export default function SingleVariantView() {
  const { hasUnsavedChanges, setHasUnsavedChanges } = useContext(appContext)
  const { productAPI } = useContext(ProductContext)
  const [isTryingToQuitWithChanges, setIsTryingToQuitWithChanges] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<string>()
  const { toastDetails, setToastDetails } = useToast()
  const { product_id, variant_id } = useParams()
  const [isVariantDataFormActive, setIsVariantDataFormActive] = useState(false)
  const navigate = useNavigate()
  const { singleProjectApiDetails, permissions: projectPermissions, clientDetails } = useContext(ProjectPageContext)

  // fetch single variant details
  const {
    fetchData: getProduct,
    data: getProductData,
    isLoading: isGetProductLoading,
    error: getProductError,
  } = productAPI

  const {
    fetchData: PatchVariant,
    isLoading: isPatchVariantLoading,
    url: patchVariantUrl,
  } = useApi<VariantsDetailData>(createCsApiUrlFromEndpoint(`products/variants/${variant_id}`))

  const clientId = clientDetails?.id

  const { clientLevelPermissions } = usePermissions({
    clientId,
    permissionSubset: [
      'PDP_APP_GET_PRODUCTS_VARIANTS_BY',
      'PDP_APP_DELETE_PRODUCTS_VARIANTS_BY',
      'PDP_APP_PATCH_PRODUCTS_VARIANTS_BY',
    ],
  })

  const [canGetVariant, canDeleteVariant, canPatchVariant] = clientLevelPermissions
  const { isInitialising: isInitialisingApis } = useInitialiser({
    subscribedApis: projectPermissions.canGetProject ? [productAPI] : [],
  })
  const isInitialising =
    isInitialisingApis || (canGetVariant && ((isGetProductLoading && !getProductData) || !getProductData))

  // get project details
  const { data: singleProjectData, error: singleProjectError } = singleProjectApiDetails
  const { project_name } = singleProjectData || {}
  const selectedVariant = useMemo(() => {
    if (!getProductData) return null
    return getProductData?.variants?.find(({ _id }) => _id === variant_id)
  }, [getProductData])

  const breadCrumbItems = useMemo(
    () => [
      {
        label: 'Projects',
        path: '/',
      },
      {
        label: project_name || 'Project',
        path: `/project/${getProductData?.project_id}`,
      },
      {
        label: getProductData?.reference_name || 'Product',
        path: `/project/${getProductData?.project_id}/product/${product_id}`,
      },
      {
        label: selectedVariant?.reference_name || 'Variant',
        path: '/',
      },
    ],
    [project_name, getProductData],
  )

  const handleRouteChange = (e: WppBreadcrumbCustomEvent<BreadcrumbItemEventDetails>) => {
    if (isInitialising) return
    const { path } = e.detail
    if (hasUnsavedChanges) {
      setPendingNavigation(path)
      setIsTryingToQuitWithChanges(true)
      return
    }
    navigate(path)
  }

  const handleVariantEdits = async (data: VariantsDetailData) => {
    if (!canPatchVariant) return
    try {
      await PatchVariant(patchVariantUrl, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      await getProduct()

      setHasUnsavedChanges(false)
      setIsVariantDataFormActive(false)
      setToastDetails({
        message: 'Edits saved successfully',
        type: 'success',
      })
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }

  const menuItems: MenuItems = useMemo(() => {
    const returnMenuItems: MenuItems = [
      {
        label: 'Download variant',
        onClick: () => console.error('TODO variant'),
      },
    ]

    if (canDeleteVariant)
      returnMenuItems.push({ label: 'Delete product', onClick: () => console.error('TODO variant') })

    return returnMenuItems
  }, [canDeleteVariant])

  if (
    getProductError?.status === 404 ||
    getProductError?.status === 500 ||
    singleProjectError?.status === 404 ||
    singleProjectError?.status === 500 ||
    (getProductData && !selectedVariant)
  )
    return <NotFound404Page />

  return (
    <>
      <div className="dark-bg-page">
        <section className={clsx('light-header', styles.header)}>
          <div className={navigationHeaderStyles.navigationHeader__container}>
            <WppBreadcrumb items={breadCrumbItems} middleTruncation onWppChange={handleRouteChange} />
            <Banner
              shouldBannerShow={!!toastDetails}
              text={toastDetails?.message}
              type={toastDetails?.type}
              onBannerClick={toastDetails?.onBannerClick}
              onClose={() => {
                if (!!toastDetails) setToastDetails(undefined)
              }}
            />
          </div>

          <PageHeader
            className={styles.titleContainer}
            isLoading={isInitialising}
            tags={[statusChecker(selectedVariant?.validation?.status).tag]}
            createdDetails={`${selectedVariant?.created_at === selectedVariant?.updated_at ? `Created: ${getFormattedDate(selectedVariant?.created_at)}` : `Updated: ${getFormattedDate(selectedVariant?.updated_at)}`} by ${
              selectedVariant?.update_info?.user_name || unknown
            }`}
            pageTitle={selectedVariant?.reference_name!}
            menuItems={menuItems}
            beforeDetails={
              selectedVariant?.master ? (
                <WppTypography type="xs-body" data-testid="region-details">
                  {masterSVG} Master variant
                </WppTypography>
              ) : null
            }
            details={{
              Product: `${getProductData?.reference_name}`,
              Region: `${getProductData?.region?.name} | ${getProductData?.region?.locale.toUpperCase().replace('_', '-')}`,
            }}
          />
        </section>

        <section className="page-content">
          {!isGetProductLoading && getProductData && selectedVariant ? (
            <VariantDataForm
              variantData={selectedVariant}
              isFormActive={isVariantDataFormActive}
              areActionsVisible={canPatchVariant}
              isPatchVariantLoading={isPatchVariantLoading}
              setHasUnsavedChanges={setHasUnsavedChanges}
              onFormEdit={() => setIsVariantDataFormActive(true)}
              onFormCancel={() => setIsVariantDataFormActive(false)}
              onFormSubmit={handleVariantEdits}
            />
          ) : (
            <FormSkeleton />
          )}
        </section>
      </div>

      {hasUnsavedChanges && isTryingToQuitWithChanges && (
        <UnsavedChangesModal
          handleSubmit={function (): void {
            setHasUnsavedChanges(false)
            setIsTryingToQuitWithChanges(false)
            setPendingNavigation(undefined)

            if (pendingNavigation) {
              navigate(pendingNavigation)
            }
          }}
          onCancel={() => {
            setPendingNavigation(undefined)
            setIsTryingToQuitWithChanges(false)
          }}
          shouldModalOpen={hasUnsavedChanges && isTryingToQuitWithChanges}
        />
      )}
    </>
  )
}
